/**
 * Data Source Attribution Components
 * ===================================
 * Shows trusted data sources throughout the platform
 * Builds trust by clearly indicating where data comes from
 */

import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { 
  Database, Link2, Shield, TrendingUp, TrendingDown, Minus,
  ExternalLink, CheckCircle2, Info, Sparkles, Building2
} from 'lucide-react';
import { DATA_SOURCES, compareToBenchmark } from '@/config/industryBenchmarks';

// Data Source Badge - shows where data comes from
export const DataSourceBadge = ({ sourceId, showName = true, size = 'sm', className = '' }) => {
  const source = DATA_SOURCES[sourceId];
  if (!source) return null;
  
  const sizeClasses = {
    xs: 'text-[9px] px-1.5 py-0.5',
    sm: 'text-[10px] px-2 py-0.5',
    md: 'text-xs px-2.5 py-1',
  };
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge 
            variant="outline" 
            className={`${sizeClasses[size]} bg-white/80 backdrop-blur-sm cursor-help ${className}`}
            style={{ borderColor: source.color + '40' }}
          >
            <Database className="w-2.5 h-2.5 mr-1" style={{ color: source.color }} />
            {showName && <span className="text-slate-600">{source.name}</span>}
            <CheckCircle2 className="w-2.5 h-2.5 ml-1 text-emerald-500" />
          </Badge>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="font-semibold">{source.name}</span>
              <Badge className="text-[9px] px-1.5 py-0" style={{ backgroundColor: source.color }}>
                {source.trustBadge}
              </Badge>
            </div>
            <p className="text-xs text-slate-500">{source.description}</p>
            {source.url && (
              <a 
                href={source.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-xs text-indigo-600 flex items-center gap-1 hover:underline"
              >
                Learn more <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// Multi-source attribution (when data comes from multiple sources)
export const MultiSourceBadge = ({ sources = [], size = 'sm', className = '' }) => {
  if (sources.length === 0) return null;
  
  const validSources = sources.filter(s => DATA_SOURCES[s]);
  if (validSources.length === 0) return null;
  
  const sizeClasses = {
    xs: 'text-[9px] px-1.5 py-0.5',
    sm: 'text-[10px] px-2 py-0.5',
    md: 'text-xs px-2.5 py-1',
  };
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge 
            variant="outline" 
            className={`${sizeClasses[size]} bg-white/80 backdrop-blur-sm cursor-help ${className}`}
          >
            <Link2 className="w-2.5 h-2.5 mr-1 text-indigo-500" />
            <span className="text-slate-600">{validSources.length} Sources</span>
            <Shield className="w-2.5 h-2.5 ml-1 text-emerald-500" />
          </Badge>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-sm">
          <div className="space-y-2">
            <p className="text-xs font-medium">Data sourced from:</p>
            <div className="flex flex-wrap gap-1">
              {validSources.map(sourceId => {
                const source = DATA_SOURCES[sourceId];
                return (
                  <Badge 
                    key={sourceId}
                    variant="outline" 
                    className="text-[9px]"
                    style={{ borderColor: source.color + '40', backgroundColor: source.color + '10' }}
                  >
                    {source.name}
                  </Badge>
                );
              })}
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// Industry Benchmark Badge - shows comparison to industry average
export const BenchmarkBadge = ({ value, benchmarkConfig, size = 'sm', className = '' }) => {
  if (!benchmarkConfig) return null;
  
  const comparison = compareToBenchmark(value, benchmarkConfig);
  const source = DATA_SOURCES[benchmarkConfig.source];
  
  const statusConfig = {
    excellent: { color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', icon: TrendingUp },
    good: { color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200', icon: TrendingUp },
    average: { color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200', icon: Minus },
    poor: { color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', icon: TrendingDown },
  };
  
  const config = statusConfig[comparison.status];
  const Icon = config.icon;
  
  const sizeClasses = {
    xs: 'text-[9px] px-1.5 py-0.5',
    sm: 'text-[10px] px-2 py-0.5',
    md: 'text-xs px-2.5 py-1',
  };
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge 
            variant="outline" 
            className={`${sizeClasses[size]} ${config.bg} ${config.border} cursor-help ${className}`}
          >
            <Icon className={`w-2.5 h-2.5 mr-1 ${config.color}`} />
            <span className={config.color}>
              {comparison.percentDiff > 0 ? '+' : ''}{comparison.percentDiff}% vs industry
            </span>
          </Badge>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="font-medium">{benchmarkConfig.metric}</span>
              <Badge className={`text-[9px] ${config.bg} ${config.color}`}>
                {comparison.status.charAt(0).toUpperCase() + comparison.status.slice(1)}
              </Badge>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <span className="text-slate-500">Your value:</span>
                <span className="ml-1 font-medium">{value}{benchmarkConfig.unit}</span>
              </div>
              <div>
                <span className="text-slate-500">Industry avg:</span>
                <span className="ml-1 font-medium">{benchmarkConfig.benchmark}{benchmarkConfig.unit}</span>
              </div>
            </div>
            <div className="text-[10px] text-slate-500">
              Industry range: {benchmarkConfig.range.low} - {benchmarkConfig.range.high}{benchmarkConfig.unit}
            </div>
            {source && (
              <div className="pt-2 border-t flex items-center gap-1 text-[10px] text-slate-500">
                <Building2 className="w-3 h-3" />
                Source: {source.name}
                {benchmarkConfig.sourceNote && (
                  <span className="text-slate-400">({benchmarkConfig.sourceNote})</span>
                )}
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// AI Insight Badge - for AI-generated content
export const AIInsightBadge = ({ confidence = 'high', size = 'sm', className = '' }) => {
  const confidenceConfig = {
    high: { color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-200', label: 'High confidence' },
    medium: { color: 'text-indigo-600', bg: 'bg-indigo-50', border: 'border-indigo-200', label: 'Medium confidence' },
    low: { color: 'text-slate-600', bg: 'bg-slate-50', border: 'border-slate-200', label: 'Exploratory' },
  };
  
  const config = confidenceConfig[confidence] || confidenceConfig.high;
  
  const sizeClasses = {
    xs: 'text-[9px] px-1.5 py-0.5',
    sm: 'text-[10px] px-2 py-0.5',
    md: 'text-xs px-2.5 py-1',
  };
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge 
            variant="outline" 
            className={`${sizeClasses[size]} ${config.bg} ${config.border} cursor-help ${className}`}
          >
            <Sparkles className={`w-2.5 h-2.5 mr-1 ${config.color}`} />
            <span className={config.color}>AI Insight</span>
          </Badge>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="font-medium">Corvana AI Analysis</span>
              <Badge className={`text-[9px] ${config.bg} ${config.color}`}>
                {config.label}
              </Badge>
            </div>
            <p className="text-xs text-slate-500">
              This insight was generated by Corvana AI based on your business data, 
              industry patterns, and market trends.
            </p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// Connected Sources Panel - shows all connected data sources
export const ConnectedSourcesPanel = ({ sources = [], compact = false }) => {
  const validSources = sources.filter(s => DATA_SOURCES[s]).map(s => DATA_SOURCES[s]);
  
  if (compact) {
    return (
      <div className="flex items-center gap-1 text-xs text-slate-500">
        <Link2 className="w-3 h-3" />
        <span>Data from {validSources.length} sources</span>
        <div className="flex -space-x-1">
          {validSources.slice(0, 3).map(source => (
            <div 
              key={source.id}
              className="w-4 h-4 rounded-full border-2 border-white flex items-center justify-center text-[8px] font-bold text-white"
              style={{ backgroundColor: source.color }}
              title={source.name}
            >
              {source.name.charAt(0)}
            </div>
          ))}
          {validSources.length > 3 && (
            <div className="w-4 h-4 rounded-full border-2 border-white bg-slate-400 flex items-center justify-center text-[8px] font-bold text-white">
              +{validSources.length - 3}
            </div>
          )}
        </div>
      </div>
    );
  }
  
  return (
    <div className="bg-slate-50 rounded-lg p-3">
      <div className="flex items-center gap-2 mb-2">
        <Shield className="w-4 h-4 text-emerald-600" />
        <span className="text-sm font-medium text-slate-900">Connected Data Sources</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {validSources.map(source => (
          <Badge 
            key={source.id}
            variant="outline"
            className="text-xs"
            style={{ borderColor: source.color + '40', backgroundColor: source.color + '10' }}
          >
            <CheckCircle2 className="w-3 h-3 mr-1" style={{ color: source.color }} />
            {source.name}
          </Badge>
        ))}
      </div>
    </div>
  );
};

export default {
  DataSourceBadge,
  MultiSourceBadge,
  BenchmarkBadge,
  AIInsightBadge,
  ConnectedSourcesPanel,
};
